'use client';
import React, { useState, FormEvent } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { createClient } from '@/lib/supabase/client';
import { CheckCircle, Lock } from 'lucide-react';
import type { PaymentIntent } from '@stripe/stripe-js';

interface CheckoutFormProps {
  clientSecret: string;
  amount: number;
  onSuccess: (paymentIntent: PaymentIntent) => void;
  onError: (message: string) => void;
}

export default function CheckoutForm({ clientSecret, amount, onSuccess, onError }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMsg('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/booking-flow`,
      },
      redirect: 'if_required',
    });

    if (error) {
      const msg = error.message || 'Payment failed. Please try again.';
      setErrorMsg(msg);
      onError(msg);
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        const supabase = createClient();
        await supabase.functions.invoke('confirm-job-payment', {
          body: { paymentIntentId: paymentIntent.id },
        });
      } catch (confirmErr) {
        console.error('Backend confirmation error:', confirmErr);
      }
      onSuccess(paymentIntent);
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={14} className="text-muted-foreground" />
          <p className="text-xs text-muted-foreground">Secured by Stripe — your card details are encrypted</p>
        </div>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {errorMsg && (
        <div className="bg-danger/10 border border-danger/20 rounded-xl px-4 py-3 text-sm text-danger">
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl gradient-primary text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <>
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Processing Payment…
          </>
        ) : (
          <>
            <CheckCircle size={16} />
            Pay £{amount.toFixed(2)} — Confirm Booking
          </>
        )}
      </button>
    </form>
  );
}
